import { BrandShareButton } from "./BrandShareButton";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Loader2, Pencil } from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Badge } from "@/components/ui/badge";
import type {
  CreativeBrandCardsProps,
  CreativeBrandGroup,
} from "@/types/creative-library";
import { PrivateImage } from "./PrivateImage";

export function CreativeBrandCards({
  brands,
  allBrands,
  creatives,
  onEdit,
  onProducts,
  onSubjects,
  onCreate,
  startingToken,
  onChanged,
}: CreativeBrandCardsProps) {
  const { t } = useTranslation("app");
  const groups = new Map<string, CreativeBrandGroup>();
  for (const brand of brands) {
    const parent = allBrands.find(
      (candidate) => candidate.token === brand.parentBrandToken,
    );
    const key = parent?.token ?? brand.token;
    const existing: CreativeBrandGroup = groups.get(key) ?? {
      key,
      name: parent?.name ?? brand.name,
      grouped: Boolean(parent),
      items: [],
    };
    existing.items.push(brand);
    existing.grouped = existing.grouped || Boolean(parent);
    groups.set(key, existing);
  }
  const brandGroups = Array.from(groups.values()).map((group) => ({
    ...group,
    items: [...group.items].sort(
      (a, b) =>
        Number(b.token === group.key) - Number(a.token === group.key) ||
        a.name.localeCompare(b.name),
    ),
  }));
  // Standalone brands share one grid so they flow side by side; only brands
  // under a parent get their own headed section.
  const sections: Array<CreativeBrandGroup> = [];
  for (const group of brandGroups) {
    const last = sections[sections.length - 1];
    if (!group.grouped && last && !last.grouped) {
      last.items.push(...group.items);
    } else {
      sections.push({ ...group, items: [...group.items] });
    }
  }
  return (
    <>
      {sections.map((group) => (
        <section key={group.key} className="col-span-full flex flex-col gap-3">
          {group.grouped && (
            <Heading level={3} size="base" className="text-muted-foreground">
              {group.name}
            </Heading>
          )}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {group.items.map((brand) => (
              <article
                key={brand.token}
                className="flex flex-col gap-4 rounded-2xl border border-border bg-background dark:bg-card p-5"
                data-testid={`creative-brand-card-${brand.token}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    {brand.parentBrandToken && (
                      <Text size="xs" color="muted">
                        {
                          allBrands.find(
                            (parent) => parent.token === brand.parentBrandToken,
                          )?.name
                        }
                      </Text>
                    )}
                    <Heading level={3} size="lg" className="break-words">
                      {brand.name}
                    </Heading>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <BrandShareButton brand={brand} onChanged={onChanged} />
                    {brand.canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(brand)}
                        aria-label={t("creativeLibrary.editBrand")}
                        data-testid={`creative-edit-brand-${brand.token}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {creatives
                  .filter(
                    (item) =>
                      item.role === "logo" &&
                      brand.materialTokens.includes(item.token),
                  )
                  .slice(0, 1)
                  .map((logo) => (
                    <PrivateImage
                      key={logo.token}
                      source={logo.mediaUrl}
                      alt={logo.title}
                      className="h-16 w-32 object-contain object-left"
                    />
                  ))}
                <div className="flex gap-2">
                  {brand.palette.map((color, index) => (
                    <span
                      key={index}
                      className="h-8 w-8 rounded-full border border-border"
                      style={{ backgroundColor: color.value }}
                      title={`${color.name}: ${color.value}`}
                    />
                  ))}
                </div>
                <Text size="sm" color="muted" className="line-clamp-3 min-h-12">
                  {brand.guidance || t("creativeLibrary.brandEditorHint")}
                </Text>
                <div className="mt-auto flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {onSubjects && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 whitespace-nowrap"
                        onClick={() => onSubjects(brand)}
                        data-testid={`creative-brand-subjects-${brand.token}`}
                      >
                        {t("creativeLibrary.subjects")}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 whitespace-nowrap"
                      onClick={() => onProducts(brand)}
                      data-testid={`creative-brand-products-${brand.token}`}
                    >
                      {t("creativeLibrary.products")}
                      <ArrowUpRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Takes the free space, so the button grows only on a row of its own. */}
                    <div className="grow-[9999]">
                      <Badge variant="outline" className="whitespace-nowrap">
                        {t(`creativeLibrary.sharing.${brand.sharingMode}`)}
                      </Badge>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="h-auto min-h-9 grow gap-2 py-1.5"
                      onClick={() => onCreate(brand)}
                      disabled={startingToken !== null}
                      data-testid={`creative-brand-create-${brand.token}`}
                    >
                      {startingToken === brand.token ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowUpRight className="h-4 w-4" />
                      )}
                      {t("creativeLibrary.createWithBrand")}
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
